import { Alert, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkoutService } from '../../services/CheckoutService';
import { D4SIGN_URL } from '../../settings';
import CardDefault from '../shared/CardDefault';

interface ExternalDocumentProps {
  teamId: string;
  transactionId: string;
  onDocumentSigned?: () => void;
  onError?: () => void;
  isSigned?: boolean;
}

interface DocumentData {
  status?: string;
  embedData?: {
    documentKey: string;
    signerEmail: string;
    signerDisplayName: string;
    signerDocumentation: string;
    signerPhone: string;
    host: string;
  };
}

// Add this at the top of the file, outside the component
const apiCallTracker = new Set<string>();
const documentDataCache = new Map<string, any>();
const listeners = new Map<string, Set<(data: DocumentData) => void>>();

const ExternalDocument = ({ teamId, transactionId, onDocumentSigned, onError, isSigned: initialIsSigned = false }: ExternalDocumentProps) => {
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [isSigned, setIsSigned] = useState(initialIsSigned);
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    description: string;
    type: 'error';
    showIcon: boolean;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Create a unique key for this specific API call
  const apiCallKey = useMemo(() => `${teamId}-${transactionId}`, [teamId, transactionId]);

  // Handle messages from D4Sign iframe
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data === "signed") {
      setIsSigned(true);
      onDocumentSigned?.();
    }
  }, [onDocumentSigned]);

  // Function to update document data
  const updateDocumentData = useCallback((data: DocumentData) => {
    setDocumentData(data);
    setIsDocumentLoading(false);

    const isDocumentSigned = data.status === 'signed' || data.status === 'finished';
    setIsSigned(isDocumentSigned);

    if (isDocumentSigned) {
      onDocumentSigned?.();
    }
  }, [onDocumentSigned]);

  useEffect(() => {
    // Check if we already have cached data
    const cachedData = documentDataCache.get(apiCallKey);
    if (cachedData) {
      console.log('Using cached document data');
      updateDocumentData(cachedData);
      return;
    }

    // Check if API call is already in progress
    if (apiCallTracker.has(apiCallKey)) {
      console.log('API call already in progress, waiting...');
      setIsDocumentLoading(true);

      // Add this component as a listener
      if (!listeners.has(apiCallKey)) {
        listeners.set(apiCallKey, new Set());
      }
      listeners.get(apiCallKey)!.add(updateDocumentData);

      return;
    }

    // Make the API call
    apiCallTracker.add(apiCallKey);
    setIsDocumentLoading(true);

    const generateDocument = async () => {
      try {
        const response = await checkoutService.generateAgreement(teamId, transactionId);

        if (response?.embedData) {
          const newDocumentData = {
            status: response.status,
            embedData: response.embedData
          };

          // Cache the data globally
          documentDataCache.set(apiCallKey, newDocumentData);

          // Update this component
          updateDocumentData(newDocumentData);

          // Notify all listeners
          const componentListeners = listeners.get(apiCallKey);
          if (componentListeners) {
            componentListeners.forEach(listener => listener(newDocumentData));
            listeners.delete(apiCallKey);
          }

          return true;
        }

        return false;
      } catch (error) {
        console.error('Error generating agreement:', error);
        setIsDocumentLoading(false);

        const statusMessage = {
          title: 'Documento para assinatura',
          description: 'Ocorreu um erro ao gerar o documento. Por favor, tente novamente mais tarde.',
          type: 'error' as const,
          showIcon: true
        };  

        if (onError) {
          onError();
        }

        setErrorMessage(statusMessage);

        documentDataCache.delete(apiCallKey);
        apiCallTracker.delete(apiCallKey);

        if (listeners.has(apiCallKey)) {
          listeners.delete(apiCallKey);
        }

        return false;
      }
    };

    generateDocument();
  }, [teamId, transactionId, apiCallKey, updateDocumentData]);

  // Cleanup listener on unmount
  useEffect(() => {
    return () => {
      const componentListeners = listeners.get(apiCallKey);
      if (componentListeners) {
        componentListeners.delete(updateDocumentData);
        if (componentListeners.size === 0) {
          listeners.delete(apiCallKey);
        }
      }
    };
  }, [apiCallKey, updateDocumentData]);

  // Initialize D4Sign embed
  useEffect(() => {
    if (!documentData || !documentData.embedData) return;

    if (documentData.status === 'finished' || documentData.status === 'signed') {
      setIsSigned(true);
      onDocumentSigned?.();
      return;
    }

    if (documentData.status === 'cancelled') {
      return;
    }

    const initializeD4SignEmbed = () => {
      try {
        // Safari fix for D4Sign
        const is_safari = navigator.userAgent.indexOf('Safari') > -1;
        const is_chrome = navigator.userAgent.indexOf('Chrome') > -1;
        const isSafari = is_safari && !is_chrome;
        const urlD4sign = D4SIGN_URL;

        if (isSafari) {
          const cookieExists = document.cookie.match(/^(.*;)?\s*fixed\s*=\s*[^;]+(.*)?$/);
          if (!cookieExists) {
            document.cookie = 'fixed=fixed; expires=Tue, 19 Jan 2038 03:14:07 UTC; path=/';
            const url = document.URL;
            const str = window.location.search;
            const param = str.replace("?", "");
            let currentUrl = url;
            if (url.indexOf("?") > -1) {
              currentUrl = url.substr(0, url.indexOf("?"));
            }
            window.location.replace(`${urlD4sign}/embed/safari_fix?param=${param}&r=${currentUrl}`);
            return;
          }
        }

        // Create iframe
        const iframe = document.createElement("iframe");
        const { embedData } = documentData;

        const embedUrl = `${embedData?.host}/${embedData?.documentKey}?email=${encodeURIComponent(embedData?.signerEmail || '')}&display_name=${encodeURIComponent(embedData?.signerDisplayName || '')}&documentation=${encodeURIComponent(embedData?.signerDocumentation || '')}&disable_preview=0`;

        iframe.setAttribute("src", embedUrl);
        iframe.setAttribute("width", "100%");
        iframe.setAttribute("height", "600");
        iframe.style.border = '1px solid #d9d9d9';
        iframe.style.borderRadius = '6px';
        iframe.setAttribute("allow", 'geolocation');

        // Clear container and add iframe
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(iframe);
          iframeRef.current = iframe;
        }

        // Add message listener for D4Sign callbacks
        window.addEventListener("message", handleMessage, false);

      } catch (error) {
        console.error('Error initializing D4Sign embed:', error);
      }
    };

    initializeD4SignEmbed();

    // Cleanup
    return () => {
      if (iframeRef.current) {
        iframeRef.current.remove();
        iframeRef.current = null;
      }
      window.removeEventListener("message", handleMessage);
    };
  }, [documentData, handleMessage, onDocumentSigned]);

  const getStatusMessage = () => {
    // If there's an error message, show it
    if (errorMessage) {
      return errorMessage;
    }

    // If document is signed (either through isSigned state or status), show success message
    if (isSigned || documentData?.status === 'signed') {
      return {
        title: 'Documento Assinado',
        description: 'O documento foi assinado com sucesso!',
        type: 'success' as const,
        showIcon: true
      };
    }

    switch (documentData?.status) {
      case 'pending_signature':
        return {
          title: 'Seu documento está pronto para assinatura',
          description: 'O documento foi processado e finalizado com sucesso!',
          type: 'success' as const,
          showIcon: true
        };
      case 'finished':
        return {
          title: 'Documento Finalizado',
          description: 'O documento foi processado e finalizado com sucesso!',
          type: 'success' as const,
          showIcon: true
        };
      case 'cancelled':
        return {
          title: 'Documento Cancelado',
          description: 'O documento foi cancelado e não pode ser assinado.',
          type: 'error' as const,
          showIcon: true
        };
      default:
        return {
          title: 'Seu documento está pronto para assinatura',
          description: 'O documento foi processado e finalizado com sucesso!',
          type: 'success' as const,
          showIcon: true
        };
    }
  };

  const statusMessage = getStatusMessage();

  // Get the dynamic title based on loading state
  const getDynamicTitle = () => {
    if (isDocumentLoading && !documentData) {
      return 'Carregando documento para assinatura...';
    }
    return statusMessage.title;
  };

  return (
    <CardDefault className='bg-white'>
      <div className="space-y-4">
        <div className='text-lg font-medium text-black'>
          <span className="flex items-center gap-3">
            {isDocumentLoading && !documentData && <Spin size="small" />}
            {getDynamicTitle()}
          </span>
        </div>

        {/* Show error message when there's an error */}
        {errorMessage && (
          <Alert
            description={errorMessage.description}
            type={errorMessage.type}
            showIcon={errorMessage.showIcon}
          />
        )}

        {/* Show signing instructions and iframe only when document is pending and not signed */}
        {documentData && documentData.status === 'pending_signature' && !isSigned && !errorMessage && (
          <div className="space-y-4">
            <Alert
              message="Atenção! É necessário assinar este documento para finalizar sua compra."
              type="warning"
            />

            <div className="w-full">
              <div
                ref={containerRef}
                style={{ minHeight: '600px' }}
              />
            </div>
          </div>
        )}

        {/* Show success message when document is signed */}
        {(isSigned || documentData?.status === 'signed' || documentData?.status === 'finished') && (
          <Alert
            description={statusMessage.description}
            type={statusMessage.type}
            showIcon={statusMessage.showIcon}
          />
        )}

        {documentData?.status === 'cancelled' && (
          <Alert
            description={statusMessage.description}
            type={statusMessage.type}
            showIcon={statusMessage.showIcon}
          />
        )}
      </div>
    </CardDefault>
  );
};

export default ExternalDocument;
